import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'

import { useHeaderLayout } from 'context/headerLayoutContext'
import AnalyticsPeriodDropdown, {
  selectedToPeriod
} from 'features/AnalyticsPeriodDropdown/AnalyticsPeriodDropdown'
import BarChart from 'features/BarChart/BarChart'
import CategoriesDropdown from 'features/CategoriesDropdown/CategoriesDropdown'
import { getLink } from 'helpers/appLinks'
import { getAnalyticsChartData } from 'helpers/dataTransformations'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getSalesAnalytics } from 'services/endpoints/analytic'
import { useUserProfile } from 'store'
import { useAnalyticsSalesCacheStore } from 'store/analyticsSalesCacheStore'
import { CHART_TYPES, type ChartType } from 'types/api/analytic'

import { SalesCard } from '../cards/SalesCard'

const hideForChartTypes = [
  CHART_TYPES.subscriptions,
  CHART_TYPES.rebills,
  CHART_TYPES.streaming_tips,
  CHART_TYPES.referral_revenue
]

type CacheEntry<T> = {
  data: T
  updatedAt: number
}

export const Sales = () => {
  const { t } = useTranslation()
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedPeriod, setSelectedPeriod] = useState<{
    start: string
    end: string
  }>(selectedToPeriod['7'])
  const [activeGraph, setActiveGraph] = useState<ChartType>()
  const currentUser = useUserProfile()
  const username = currentUser?.username ?? ''

  const selectedCategoryId = selectedCategory || undefined

  const {
    getCache: getSalesCache,
    setCache: setSalesCache,
    hydrated
  } = useAnalyticsSalesCacheStore()

  const getPeriodKey = (period: { start: string; end: string }) =>
    `${new Date(period.start).toISOString().slice(0, 10)}_${new Date(period.end).toISOString().slice(0, 10)}`

  const salesCacheKey = useMemo(() => {
    return `sales-${selectedCategory || 'all'}-${getPeriodKey(selectedPeriod)}`
  }, [selectedCategory, selectedPeriod])

  const graphCacheKey = useMemo(() => {
    return `graph-${activeGraph ?? 'none'}-${selectedCategory || 'all'}-${getPeriodKey(selectedPeriod)}`
  }, [activeGraph, selectedCategory, selectedPeriod])

  const cachedSalesEntry = useMemo<CacheEntry<any> | undefined>(() => {
    const entry = getSalesCache(salesCacheKey)
    return entry && typeof entry === 'object' ? entry : undefined
  }, [getSalesCache, salesCacheKey])

  const cachedGraphEntry = useMemo<CacheEntry<any> | undefined>(() => {
    const entry = getSalesCache(graphCacheKey)
    return entry && typeof entry === 'object' ? entry : undefined
  }, [getSalesCache, graphCacheKey])

const {
  data: salesData,
  isFetching: isFetchingSales,
  refetch: refetchSales
} = useQuery<any>(
  ['salesAnalytics', selectedCategory, selectedPeriod],
  async () => {
    const fresh = await getSalesAnalytics({
      dateFrom: selectedPeriod?.start,
      dateTo: selectedPeriod?.end,
      categoryId: selectedCategoryId || undefined
    })

    if (fresh) {
      setSalesCache(salesCacheKey, fresh)
    }

    return fresh
  },
  {
    enabled: hydrated,
    keepPreviousData: true,
    refetchOnMount: true,
    staleTime: 60_000,
    initialData: hydrated ? cachedSalesEntry?.data : undefined,
    initialDataUpdatedAt: hydrated
      ? cachedSalesEntry?.updatedAt
      : undefined
  }
)

const {
  data: graphData,
  isFetching: isFetchingGraph,
  refetch: refetchGraph
} = useQuery<any>(
  ['salesAnalyticsGraph', activeGraph, selectedCategory, selectedPeriod],
  async () => {
    const fresh = await getAnalyticsChartData({
      fetchPromise: getSalesAnalytics({
        dateFrom: selectedPeriod?.start,
        dateTo: selectedPeriod?.end,
        categoryId: selectedCategoryId || undefined,
        chart: activeGraph
      })
    })

    if (fresh) {
      setSalesCache(graphCacheKey, fresh)
    }

    return fresh
  },
  {
    enabled: hydrated && !!activeGraph,
    keepPreviousData: true,
    refetchOnMount: true,
    staleTime: 60_000,
    initialData: hydrated ? cachedGraphEntry?.data : undefined,
    initialDataUpdatedAt: hydrated
      ? cachedGraphEntry?.updatedAt
      : undefined
  }
)
const periodKey = useMemo(
  () => getPeriodKey(selectedPeriod),
  [selectedPeriod]
)

const previousPeriodKeyRef = useRef(periodKey)

useEffect(() => {
  if (!hydrated) return

  const periodChanged = previousPeriodKeyRef.current !== periodKey

  previousPeriodKeyRef.current = periodKey

  if (!periodChanged) return

  // Explicit refetch bypasses staleTime.
  void refetchSales()

  if (activeGraph) {
    void refetchGraph()
  }
}, [
  periodKey,
  hydrated,
  activeGraph,
  refetchSales,
  refetchGraph
])
  const salesLoaded =
  hydrated &&
  salesData !== undefined &&
  !isFetchingSales


  
  const cards = useMemo(
    () => [
      {
        key: CHART_TYPES.overall_revenue,
        label: t('overallRevenue'),
        link: getLink.sales()
      },
      {
        key: CHART_TYPES.subscriptions,
        label: t('subscriptions'),
        link: getLink.analyticsReports({
          categoryId: selectedCategory,
          dateFrom: selectedPeriod.start,
          dateTo: selectedPeriod.end,
          tab: 'subscription'
        })
      },
      {
        key: CHART_TYPES.rebills,
        label: t('rebills'),
        link: '/earnings/sales'
      },
      {
        key: CHART_TYPES.streaming_tips,
        label: t('streams'),
        link: getLink.analyticsReports({
          categoryId: selectedCategory,
          dateFrom: selectedPeriod.start,
          dateTo: selectedPeriod.end,
          tab: 'streams'
        })
      },
      {
        key: CHART_TYPES.referral_revenue,
        label: t('referrals'),
        link: '/referrals/summary'
      },
      {
        key: CHART_TYPES.premium_sales,
        label: t('premiumSales'),
        link: getLink.analyticsReports({
          categoryId: selectedCategory,
          dateFrom: selectedPeriod.start,
          dateTo: selectedPeriod.end,
          tab: 'content',
          subTab: 'premium'
        })
      },
      {
        key: CHART_TYPES.message_unlocks,
        label: t('messageUnlocks'),
        link: getLink.analyticsReports({
          categoryId: selectedCategory,
          dateFrom: selectedPeriod.start,
          dateTo: selectedPeriod.end,
          tab: 'messages'
        })
      },
      {
        key: CHART_TYPES.content_tips,
        label: t('contentTips'),
        link: getLink.analyticsReports({
          categoryId: selectedCategory,
          dateFrom: selectedPeriod.start,
          dateTo: selectedPeriod.end,
          tab: 'content',
          subTab: 'all'
        })
      }
    ],
    [selectedCategory, selectedPeriod.end, selectedPeriod.start, t]
  )
useEffect(() => {
  let lastTop = window.scrollY
  let ticking = false
  let hasUserScrolled = false

  // Hide immediately on first downward movement
  const SHOW_AT_TOP_PX = 0
  const DELTA_PX = 1

  const setHeaderNavHidden = (hidden: boolean) => {
    document.body.classList.toggle('header-nav-hidden', hidden)
  }

  const enableScrollAnimation = () => {
    if (!document.body.classList.contains('header-scroll-animating')) {
      document.body.classList.add('header-scroll-animating')
    }
  }

  const resetHeaderNav = () => {
    document.body.classList.remove('header-nav-hidden')
    document.body.classList.remove('header-scroll-animating')
    document.body.classList.remove('header-nav-ready')
  }

  // Reset on mount
  resetHeaderNav()

  // Enable transitions after first paint only
  requestAnimationFrame(() => {
    document.body.classList.add('header-nav-ready')
  })

  const onScroll = () => {
    hasUserScrolled = true

    const top = window.scrollY
    const delta = top - lastTop

    if (!ticking) {
      ticking = true

      requestAnimationFrame(() => {
        ticking = false

        if (!hasUserScrolled) return

        enableScrollAnimation()

        // Fully visible only at exact top
        if (top <= SHOW_AT_TOP_PX) {
          setHeaderNavHidden(false)
          lastTop = top
          return
        }

        // Ignore micro jitter
        if (Math.abs(delta) < DELTA_PX) {
          lastTop = top
          return
        }

        // Hide immediately when scrolling down
        // Show immediately when scrolling up
        setHeaderNavHidden(delta > 0)

        lastTop = top
      })
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  return () => {
    window.removeEventListener('scroll', onScroll)
    resetHeaderNav()
  }
}, [])
  return (
    <div className='sales-parent'>
      {activeGraph && (
        <div className='analytics-chart-container'>
          <BarChart
            chartData={graphData}
            datasetOptions={{
              borderRadius: 15,
              barThickness: 'flex'
            }}
          />
        </div>
      )}

      <WithHeaderSection
        customClass={`analytics-sales-whs${activeGraph ? ' analytics-sales-whs--chart-open' : ''}`}
        headerSection={
          <div className='analytics-dropdown-container'>
            <div style={{ width: '50%', marginRight: '5px' }}>
              <CategoriesDropdown
                username={username}
                selectedCategoryId={selectedCategory}
                onSelectCategory={setSelectedCategory}
              />
            </div>
            <div style={{ width: '50%' }}>
              <AnalyticsPeriodDropdown
                selectedPeriodId={'7'}
                onSelectPeriod={setSelectedPeriod}
              />
            </div>
          </div>
        }
      >
        <div className='analytics-cards-container'>
          {cards.map(card => {
            const cardData = salesData?.[card.key]
            if (!cardData) return null

            if (
              selectedCategoryId !== undefined &&
              hideForChartTypes.includes(card.key as any)
            ) {
              return null
            }

            return (
              <SalesCard
                key={card.key}
                cardKey={card.key}
                activeGraph={activeGraph}
                label={card.label}
                sum={cardData?.sum}
                percentage={cardData?.percentage}
                salesCount={cardData?.count}
                onGraphClick={key => {
                  setActiveGraph(prev =>
                    prev === key ? undefined : (key as ChartType)
                  )
                }}
                link={card.link}
                linkInArrow={true}
              />
            )
          })}
        </div>
      </WithHeaderSection>
    </div>
  )
}